import { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

import { SecretsError, secretsRequest } from './api';

type Profile = { archived?: boolean; devices?: string[] | null; name: string; revision?: string; status: string; updated?: string };
type Revision = { _id: string; action: string; actor: string; created: string };
type Audit = { action: string; actor: string; created: string; outcome: string };

const errorMessage = (error: unknown) => {
  if (error instanceof SecretsError && error.status === 401) return 'Your session has expired. Sign in again to access Secrets.';
  if (error instanceof SecretsError && error.status === 409)
    return 'The profile changed or is still assigned to devices. Reload and review before trying again.';
  if (error instanceof SecretsError && error.status === 400)
    return 'Invalid profile name or YAML. Use a nonempty mapping, without aliases or interpolation, up to 128 KiB.';
  return 'Secrets management is unavailable. Your changes were not confirmed; reload before retrying.';
};

function ProfileEditor({ profile, onClose, onSaved }: { onClose: () => void; onSaved: () => void; profile: Profile }) {
  const [name, setName] = useState(profile.name);
  const [yaml, setYaml] = useState('');
  const [revealed, setRevealed] = useState(!profile.name);
  const [revision, setRevision] = useState(profile.revision || 'new');
  const [history, setHistory] = useState<Revision[]>([]);
  const [audit, setAudit] = useState<Audit[]>([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<{ action: 'archive' | 'restore'; revision?: string } | null>(null);
  const controller = useRef(new AbortController());
  useEffect(() => {
    const current = controller.current;
    return () => current.abort();
  }, []);
  const path = `/profiles/${encodeURIComponent(name)}`;
  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await operation();
    } catch (failure) {
      if (!controller.current.signal.aborted) {
        setError(errorMessage(failure));
        if (failure instanceof SecretsError && failure.status === 401) {
          setYaml('');
          setRevealed(false);
        }
      }
    } finally {
      if (!controller.current.signal.aborted) setBusy(false);
    }
  };
  const call = (suffix: string, method = 'GET', body?: object, version?: string) => secretsRequest(suffix, method, body, version, controller.current.signal);
  const reveal = () =>
    run(async () => {
      const result = await call(`${path}/reveal`, 'POST', {});
      setYaml(result.yaml);
      setRevision(result.revision);
      setRevealed(true);
    });
  const showHistory = () =>
    run(async () => {
      const [versions, events] = await Promise.all([call(`${path}/history`), call(`${path}/audit`)]);
      setHistory(versions.revisions);
      setAudit(events.events);
    });
  const moreHistory = () =>
    run(async () => {
      const [versions, events] = await Promise.all([call(`${path}/history?offset=${history.length}`), call(`${path}/audit?offset=${audit.length}`)]);
      setHistory(previous => [...previous, ...versions.revisions]);
      setAudit(previous => [...previous, ...events.events]);
      if (!versions.revisions.length && !events.events.length) setMessage('All retained history is shown.');
    });
  const save = () =>
    run(async () => {
      await call(path, 'PUT', { yaml }, revision);
      setYaml('');
      onSaved();
    });
  const confirm = () =>
    run(async () => {
      if (!confirmation) return;
      await call(`${path}/${confirmation.action}`, 'POST', { revision: confirmation.revision }, revision);
      setYaml('');
      onSaved();
    });
  return (
    <Dialog className="z2-secrets-private" open fullWidth maxWidth="lg" onClose={busy ? undefined : onClose}>
      <DialogTitle>{profile.name ? `Profile: ${profile.name}` : 'Create environment profile'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {message && <Alert severity="success">{message}</Alert>}
          <TextField
            label="Profile name"
            value={name}
            disabled={!!profile.name || busy}
            onChange={event => setName(event.target.value)}
            helperText="Lowercase letters, numbers, underscores and hyphens; up to 64 characters."
          />
          {!revealed && !profile.archived && (
            <Button disabled={busy} onClick={reveal}>
              Reveal and edit YAML
            </Button>
          )}
          {revealed && (
            <TextField
              label="Profile YAML"
              value={yaml}
              multiline
              minRows={12}
              maxRows={24}
              disabled={busy}
              autoComplete="off"
              onChange={event => setYaml(event.target.value)}
              slotProps={{ htmlInput: { spellCheck: false, 'data-private': true, 'data-sentry-mask': true } }}
              sx={{ '& textarea': { fontFamily: 'monospace' } }}
            />
          )}
          <Typography variant="body2">
            Saving updates the server profile. Devices receive changes on their next credential fetch; applications are not restarted.
          </Typography>
          <Stack direction="row" spacing={1}>
            {revealed && (
              <Button
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    await call('/validate', 'POST', { yaml });
                    setMessage('YAML is valid.');
                  })
                }
              >
                Validate YAML
              </Button>
            )}
            {profile.name && (
              <Button disabled={busy} onClick={showHistory}>
                Version history and audit
              </Button>
            )}
            {profile.name && !profile.archived && (
              <Button
                color="warning"
                disabled={busy || profile.devices == null || profile.devices.length > 0}
                onClick={() => setConfirmation({ action: 'archive' })}
              >
                Archive profile
              </Button>
            )}
          </Stack>
          {history.length > 0 && (
            <>
              <Typography variant="h6">Encrypted version history</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Changed</TableCell>
                    <TableCell>Actor</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Restore</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map(item => (
                    <TableRow key={item._id}>
                      <TableCell>{item.created}</TableCell>
                      <TableCell>{item.actor}</TableCell>
                      <TableCell>{item.action}</TableCell>
                      <TableCell>
                        <Button disabled={busy} onClick={() => setConfirmation({ action: 'restore', revision: item._id })}>
                          Restore version
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
          {(history.length >= 200 || audit.length >= 200) && (
            <Button disabled={busy} onClick={moreHistory}>
              Load older history
            </Button>
          )}
          {audit.length > 0 && (
            <>
              <Typography variant="h6">Audit trail</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Actor</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Outcome</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audit.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.created}</TableCell>
                      <TableCell>{item.actor}</TableCell>
                      <TableCell>{item.action}</TableCell>
                      <TableCell>{item.outcome}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
        {revealed && (
          <Button variant="contained" disabled={busy || !name || !yaml} onClick={save}>
            Save profile
          </Button>
        )}
      </DialogActions>
      <Dialog open={!!confirmation} onClose={() => setConfirmation(null)}>
        <DialogTitle>{confirmation?.action === 'archive' ? 'Archive this profile?' : 'Restore this version?'}</DialogTitle>
        <DialogContent>
          {confirmation?.action === 'archive'
            ? 'Future credential fetches will stop. Encrypted history is retained. Devices must be unassigned first.'
            : 'This creates a new active revision. Devices are not refreshed automatically.'}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmation(null)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={confirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default function Secrets() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selected, setSelected] = useState<Profile | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    secretsRequest('/profiles', 'GET', undefined, undefined, controller.signal)
      .then(result => setProfiles(result.profiles))
      .catch(failure => {
        if (!controller.signal.aborted) {
          setError(errorMessage(failure));
          setSelected(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reload]);
  return (
    <Box sx={{ maxWidth: 1200 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Secrets</Typography>
        <Stack direction="row" spacing={1}>
          <Button disabled={loading} onClick={() => setReload(value => value + 1)}>
            Reload
          </Button>
          <Button variant="contained" disabled={loading || !!error} onClick={() => setSelected({ name: '', status: 'new', devices: [] })}>
            Create profile
          </Button>
        </Stack>
      </Stack>
      <Typography sx={{ mb: 2 }}>
        Manage environment profiles delivered to devices with an env tag. All authenticated Mender users can access these profiles.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <Typography>Loading profiles…</Typography>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Profile</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last change</TableCell>
              <TableCell>Referencing devices</TableCell>
              <TableCell>Manage</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map(profile => (
              <TableRow key={profile.name}>
                <TableCell>{profile.name}</TableCell>
                <TableCell>
                  <Chip size="small" label={profile.status} />
                </TableCell>
                <TableCell>{profile.updated || '—'}</TableCell>
                <TableCell>{profile.devices == null ? 'Unavailable' : profile.devices.length ? profile.devices.join(', ') : 'None'}</TableCell>
                <TableCell>
                  <Button disabled={!profile.revision} onClick={() => setSelected(profile)}>
                    Manage profile
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {!loading && !error && !profiles.length && <Typography sx={{ mt: 2 }}>No environment profiles yet.</Typography>}
      {selected && (
        <ProfileEditor
          key={selected.name}
          profile={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            setReload(value => value + 1);
          }}
        />
      )}
    </Box>
  );
}
